import { useCallback, useEffect, useState } from "react";

/**
 * MYID-D1 (2026-05-24) — lifted from karePortal apps/next/src/hooks/useA11yPrefs.ts
 * (KP9-12/13/14). Three independent accessibility toggles persisted to
 * localStorage and applied to the <html> element as CSS classes:
 *
 *   - .hc           → high-contrast theme (KP9-12)
 *   - .text-lg-mode → larger root font-size (scales rems) (KP9-13)
 *   - .no-motion    → kills transitions + animations (KP9-14)
 *
 * Classes are defined in src/globals.css; the hook only writes the
 * class names. LocalStorage with cross-tab sync via the `storage` event.
 *
 * Surfaced in AccountDisplayPrefs page (the natural home — that page
 * already handles theme + landing route + widget order).
 *
 * No KC writethrough: a11y prefs are device-affinity (different users
 * may share a device, and the visual setup is per-device anyway). Same
 * call karePortal made.
 */

const STORAGE_KEY = "myCollabkareId:a11y";

interface A11yPrefs {
  hc: boolean;
  largeText: boolean;
  reducedMotion: boolean;
}

const DEFAULTS: A11yPrefs = {
  hc: false,
  largeText: false,
  // reducedMotion default = false; the .no-motion class is the explicit
  // OPT-IN. globals.css's @media (prefers-reduced-motion: reduce) block
  // independently honors the OS-level preference regardless of this flag.
  reducedMotion: false,
};

function readPrefs(): A11yPrefs {
  if (typeof window === "undefined") return DEFAULTS;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULTS;
    const parsed = JSON.parse(raw) as Partial<A11yPrefs>;
    return {
      hc: typeof parsed.hc === "boolean" ? parsed.hc : DEFAULTS.hc,
      largeText:
        typeof parsed.largeText === "boolean"
          ? parsed.largeText
          : DEFAULTS.largeText,
      reducedMotion:
        typeof parsed.reducedMotion === "boolean"
          ? parsed.reducedMotion
          : DEFAULTS.reducedMotion,
    };
  } catch {
    return DEFAULTS;
  }
}

function writePrefs(prefs: A11yPrefs): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // Quota exceeded / private mode — silently ignore.
  }
}

function applyClasses(prefs: A11yPrefs): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.classList.toggle("hc", prefs.hc);
  root.classList.toggle("text-lg-mode", prefs.largeText);
  root.classList.toggle("no-motion", prefs.reducedMotion);
}

export interface UseA11yPrefsResult extends A11yPrefs {
  setHc: (v: boolean) => void;
  setLargeText: (v: boolean) => void;
  setReducedMotion: (v: boolean) => void;
}

/**
 * Call from any component that needs to read or change the prefs.
 * Multiple consumers stay in sync via the localStorage `storage` event.
 */
export function useA11yPrefs(): UseA11yPrefsResult {
  const [prefs, setPrefs] = useState<A11yPrefs>(() => readPrefs());

  useEffect(() => {
    applyClasses(prefs);
  }, [prefs]);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setPrefs(readPrefs());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const update = useCallback((patch: Partial<A11yPrefs>) => {
    setPrefs((curr) => {
      const next = { ...curr, ...patch };
      writePrefs(next);
      return next;
    });
  }, []);

  return {
    ...prefs,
    setHc: (v) => update({ hc: v }),
    setLargeText: (v) => update({ largeText: v }),
    setReducedMotion: (v) => update({ reducedMotion: v }),
  };
}

/**
 * Apply-on-mount variant — useful in the root App component so a11y
 * classes land on <html> even before any preferences page is opened.
 * No subscription / no state — just reads + applies once. Cheap.
 */
export function useApplyA11yOnMount(): void {
  useEffect(() => {
    applyClasses(readPrefs());
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) applyClasses(readPrefs());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);
}
